'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { ProductCardFragment } from '@gql-generated/graphql'
import { useCallback, useId, useState } from 'react'
import { useSelectable } from './SelectableContext.client'

export type SelectableItemProps = React.PropsWithChildren & {
  /** The product's reference */
  reference: ProductCardFragment['reference']

  /** Additional classes to add to the component */
  className?: string

  /** Whether or not the product is disabled */
  isDisabled?: boolean

  /** The function to call when the product is selected */
  onChange?: (isSelected: boolean) => void
}

export function SelectableItem({
  reference,
  className,
  children,
  onChange,
}: SelectableItemProps): React.ReactElement | null {
  const htmlId = useId()
  const { t } = useTranslation(['selectable'])
  const { items, toggleItem, isDisabled } = useSelectable()!
  const [isSelected, setIsSelected] = useState(true)

  const item = items.find((item) => item.reference === reference)

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsSelected(!isSelected)
      toggleItem?.(reference)
      onChange?.(!isSelected)
      // Blur so :focus-within releases the card's hover state (stroke/CTA/stock fade) after toggling, same as the product-card add-to-cart button
      event.target.blur()
    },
    [reference, isSelected, toggleItem, onChange],
  )

  if (!item || !item.name) {
    return null
  }

  return (
    <div className={twMerge(className, 'relative')}>
      <input hidden name={item.name ?? undefined} value={item.reference} readOnly />
      <input
        id={htmlId}
        className="peer absolute left-1 top-1 z-base"
        type="checkbox"
        aria-checked={isSelected}
        checked={isSelected}
        onChange={handleOnChange}
        disabled={isDisabled}
      />
      <label
        htmlFor={htmlId}
        className="absolute bottom-0 left-0 right-0 top-0 z-base cursor-pointer desktop:bottom-auto desktop:h-[11.5rem]"
      >
        <span className="sr-only">
          {t(`selectable:label.title.${isSelected}`, {
            name: item.name,
          })}
        </span>
      </label>
      <div
        className={twMerge(
          // Dim unselected items at rest, full opacity once selected or on keyboard focus within
          'opacity-50 transition-opacity peer-checked:opacity-100 has-[:focus-visible]:opacity-100',
        )}
      >
        {children}
      </div>
    </div>
  )
}
